import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_PREFERENCES_KEY = "cookie_preferences";

const DEFAULT_PREFERENCES = {
  essential: true,
  analytics: false,
  fingerprint: false,
};

const EU_COUNTRY_CODES = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
]);

const parseStoredPreferences = (value) => {
  try {
    const parsed = JSON.parse(value);
    return {
      essential: true,
      analytics: Boolean(parsed?.analytics),
      fingerprint: Boolean(parsed?.fingerprint),
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

const detectEuUser = () => {
  if (typeof navigator === "undefined" || typeof Intl === "undefined") {
    return false;
  }

  const localeCandidates = [navigator.language, ...(navigator.languages || [])]
    .filter(Boolean)
    .map((locale) => String(locale).split("-").pop()?.toUpperCase())
    .filter(Boolean);

  if (
    localeCandidates.some((countryCode) => EU_COUNTRY_CODES.has(countryCode))
  ) {
    return true;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  return timezone.startsWith("Europe/");
};

const emitPreferencesUpdate = (preferences) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("cookie-preferences-updated", {
      detail: preferences,
    }),
  );
};

function PreferenceToggle({
  title,
  description,
  checked,
  disabled = false,
  onChange,
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-white/20 bg-white/10 px-3 py-2">
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="text-xs text-white/70">{description}</p>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition-colors ${
          checked ? "bg-green-400" : "bg-gray-300"
        } ${disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
        aria-pressed={checked}
        aria-label={`${title} toggle`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const storedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (storedPreferences) {
      setPreferences(parseStoredPreferences(storedPreferences));
    }

    if (storedConsent) {
      setIsVisible(false);
      return;
    }

    if (detectEuUser()) {
      setIsVisible(true);
    }
  }, []);

  const canRender = useMemo(() => isVisible, [isVisible]);

  const persistConsent = (consentValue, nextPreferences) => {
    const normalized = {
      essential: true,
      analytics: Boolean(nextPreferences?.analytics),
      fingerprint: Boolean(nextPreferences?.fingerprint),
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, consentValue);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(normalized));
    emitPreferencesUpdate(normalized);
    setPreferences(normalized);
    setIsVisible(false);
    setShowPreferences(false);
  };

  if (!canRender) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 px-2 sm:px-4">
      <div className="pointer-events-auto mx-auto w-full max-w-4xl rounded-2xl border border-green-700 bg-green-900 p-4 text-white shadow-2xl">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h3 className="text-base font-semibold sm:text-lg">
              Your cookie choices
            </h3>
            <p className="mt-1 text-sm text-white/80">
              We use essential cookies to run Vouchee and optional cookies for
              analytics and fingerprint-based fraud protection. Read more in our{" "}
              <Link
                to="/privacy"
                className="text-green-300 underline decoration-green-300/80 underline-offset-2 hover:text-green-200"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          {showPreferences && (
            <div className="grid gap-2">
              <PreferenceToggle
                title="Essential"
                description="Required for authentication, security, and core functionality."
                checked
                disabled
                onChange={() => {}}
              />
              <PreferenceToggle
                title="Analytics"
                description="Helps us understand usage and improve product quality."
                checked={preferences.analytics}
                onChange={(value) =>
                  setPreferences((prev) => ({ ...prev, analytics: value }))
                }
              />
              <PreferenceToggle
                title="Fingerprint tracking"
                description="Used for fraud prevention and referral abuse protection."
                checked={preferences.fingerprint}
                onChange={(value) =>
                  setPreferences((prev) => ({ ...prev, fingerprint: value }))
                }
              />
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            {showPreferences && (
              <button
                type="button"
                onClick={() => persistConsent("customized", preferences)}
                className="rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-400"
              >
                Save Preferences
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowPreferences((prev) => !prev)}
              className="rounded-xl border border-white/40 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              Manage Preferences
            </button>
            <button
              type="button"
              onClick={() =>
                persistConsent("accepted", {
                  essential: true,
                  analytics: true,
                  fingerprint: true,
                })
              }
              className="rounded-xl bg-green-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-400"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
